import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ToastV2Service, retryAfterTimeout } from '@schenck/rotec-components';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { ReportTemplateAssignment } from 'src/dto/report-template/report-template-assignment';
import { apiBaseUrl, apiVersion } from '../config';
import { PaginationResult } from '../dto/pagination-result';
import {
  PdfExportPreview,
  ReportTemplate,
  ReportTemplateFieldDefinition,
  ReportTemplateFilter,
  ReportTemplatePreview,
} from '../dto/report-template';
import { ErrorHandlerService } from 'src/services/error-handler/error-handler.service';

@Injectable()
export class ReportTemplateApi {
  private readonly http = inject(HttpClient);
  private readonly toastService = inject(ToastV2Service);
  private readonly errorHandlerService = inject(ErrorHandlerService);

  private readonly _route: string = `${apiBaseUrl}/reporttemplate`;
  private _previewsLoading$ = new BehaviorSubject<boolean>(false);

  public get previewsLoading$(): Observable<boolean> {
    return this._previewsLoading$.asObservable();
  }

  /**
   * Creates or updates a report template in the backend. If the report template has an `id`, an update operation is performed.
   * Otherwise the backend executes a create operation.
   *
   * @param reportTemplate the report template to create/update
   *
   * @returns the created/updated report template
   */
  createOrUpdate(reportTemplate: ReportTemplate): Observable<ReportTemplate> {
    return this.http.put(`${this._route}?api-version=${apiVersion}`, reportTemplate).pipe(
      retryAfterTimeout(2000, 5),
      map((result: ReportTemplate) => result)
    );
  }

  /**
   * Clones a report template in the backend. As source, the report template identified by the supplied `id` is used.
   *
   * @param id the identifier of the report template to clone
   *
   * @returns the cloned report template
   */
  clone(id: string): Observable<ReportTemplate> {
    return this.http.post(`${this._route}/${id}?api-version=${apiVersion}`, {}).pipe(
      retryAfterTimeout(2000, 5),
      map((result: ReportTemplate) => result)
    );
  }

  /**
   * Deletes a report template in the backend.
   *
   * @param id the identifier of the report template
   *
   * @returns true if the delete operation was successful, false otherwise
   */
  delete(id: string): Observable<boolean> {
    return this.http.delete(`${this._route}/${id}?api-version=${apiVersion}`).pipe(
      retryAfterTimeout(2000, 5),
      map((result: boolean) => result)
    );
  }

  /***
   * Gets a report template from the backend.
   *
   * @param id the identifier of the report template
   *
   * @returns the report template or null, if none was found
   */
  get(id: string): Observable<ReportTemplate> {
    return this.http.get(`${this._route}/${id}?api-version=${apiVersion}`).pipe(
      retryAfterTimeout(2000, 5),
      map((result: ReportTemplate) => result)
    );
  }

  /**
   * Gets the number of report templates stored in the backend.
   *
   * @returns the number of report templates stored in the backend.
   */
  getNumberOfTemplates(): Observable<number> {
    return this.http.get(`${this._route}/count?api-version=${apiVersion}`).pipe(
      retryAfterTimeout(2000, 5),
      map((result: number) => result)
    );
  }

  /**
   * Gets the report template previews according to the specified filter. Report template previews are slimmed down report templates
   * with only a few properties, supposed to be presented to the user as some kind of selection to choose from.
   *
   * @returns the report template previews wrapped in a pagination-aware container object
   */
  getPreviews(filter?: ReportTemplateFilter): Observable<PaginationResult<ReportTemplatePreview>> {
    this._previewsLoading$.next(true);
    return this.http.post(`${this._route}/preview?api-version=${apiVersion}`, filter).pipe(
      retryAfterTimeout(2000, 5),
      catchError((error) => {
        this.toastService.openError();
        this.errorHandlerService.setTemplateApiError(error?.error?.Message || true);
        throw error;
      }),
      tap(() => this.errorHandlerService.setTemplateApiError(false)),
      map((result: PaginationResult<ReportTemplatePreview>) => result),
      finalize(() => this._previewsLoading$.next(false))
    );
  }

  getFieldDefinitions(): Observable<ReportTemplateFieldDefinition[]> {
    return this.http.get(`${this._route}/field-definitions?api-version=${apiVersion}`).pipe(
      retryAfterTimeout(2000, 5),
      map((result: ReportTemplateFieldDefinition[]) => result)
    );
  }

  /**
   * Creates or updates a report template in the backend. If the report template has an `id`, an update operation is performed.
   * Otherwise the backend executes a create operation.
   *
   * @param reportTemplate the report template to create/update
   *
   * @returns the created/updated report template
   */
  setTemplateAssignments(
    reportTemplateAssignments: ReportTemplateAssignment[]
  ): Observable<ReportTemplateAssignment[]> {
    return this.http
      .put(`${this._route}/assignments?api-version=${apiVersion}`, reportTemplateAssignments)
      .pipe(
        retryAfterTimeout(2000, 5),
        map((result: ReportTemplateAssignment[]) => result)
      );
  }

  getTemplateAssignments(): Observable<ReportTemplateAssignment[]> {
    return this.http.get(`${this._route}/assignments?api-version=${apiVersion}`).pipe(
      retryAfterTimeout(2000, 5),
      map((result: ReportTemplateAssignment[]) => result)
    );
  }

  /**
   * Exports a preview PDF based on the supplied pdf preview configuration.
   *
   * @param pdfExportPreview the configuration settings to use for exporting the PDF preview.
   * @returns an Observable that wraps around the exported PDF.
   */
  exportPreviewPdf(pdfExportPreview: PdfExportPreview): Observable<Blob> {
    return this.http
      .post(`${this._route}/export-preview-pdf?api-version=${apiVersion}`, pdfExportPreview, {
        responseType: 'blob',
      })
      .pipe(
        retryAfterTimeout(2000, 5),
        map((result: Blob) => result)
      );
  }
}
